function firstPresentId(...values) {
  for (const value of values) {
    if (value === 0 || value === "0") return value;
    if (value == null) continue;
    const text = String(value).trim();
    if (!text || text === "null" || text === "undefined" || text === "—") {
      continue;
    }
    return value;
  }
  return null;
}

/** Normalize list-row fields used to open commission detail. */
export function resolveCommissionDetailIds(application = {}) {
  return {
    studentId: firstPresentId(
      application.clientid,
      application.client_id,
      application.clientId,
      application.student_id,
      application.studentId,
    ),
    appId: firstPresentId(
      application.application_id,
      application.applicationId,
      application.app_id,
      application.appId,
    ),
    providerId: firstPresentId(
      application.providerid,
      application.provider_id,
      application.providerId,
    ),
    courseId: firstPresentId(
      application.courseid,
      application.course_id,
      application.courseId,
    ),
    applicationCourseId: firstPresentId(
      application.application_course_id,
      application.applicationcourseid,
      application.eduappcourse_id,
    ),
    type:
      firstPresentId(
        application.category,
        application.type,
        application.application_type,
        application.applicationType,
      ) || "education",
  };
}

export function commissionDetailPath(application) {
  const ids = resolveCommissionDetailIds(application);
  const params = new URLSearchParams({
    student_id: String(ids.studentId ?? ""),
    app_id: String(ids.appId ?? ""),
    provider_id: String(ids.providerId ?? ""),
    course_id: String(ids.courseId ?? ""),
    type: String(ids.type ?? "education"),
  });

  if (ids.applicationCourseId != null && String(ids.applicationCourseId).trim() !== "") {
    params.set(
      "application_course_id",
      String(ids.applicationCourseId),
    );
  }

  return `/commissions/detail?${params.toString()}`;
}

export function canOpenCommissionDetail(application) {
  const ids = resolveCommissionDetailIds(application);
  const hasCourse =
    ids.courseId != null ||
    ids.applicationCourseId != null;
  return (
    ids.appId != null &&
    ids.studentId != null &&
    ids.providerId != null &&
    hasCourse &&
    Boolean(ids.type)
  );
}

/** Tooltip when the detail action is disabled. */
export function getCommissionDetailDisabledReason(application) {
  const ids = resolveCommissionDetailIds(application);
  const missing = [];
  if (ids.appId == null) missing.push("application");
  if (ids.studentId == null) missing.push("student");
  if (ids.providerId == null) missing.push("provider");
  if (ids.courseId == null && ids.applicationCourseId == null) missing.push("course");
  if (!ids.type) missing.push("type");
  if (!missing.length) return "";
  return `Cannot open detail — missing ${missing.join(", ")}`;
}

export function parseCommissionDetailParams(searchParams) {
  return {
    studentId: searchParams.get("student_id"),
    appId: searchParams.get("app_id"),
    providerId: searchParams.get("provider_id"),
    courseId: searchParams.get("course_id"),
    applicationCourseId: searchParams.get("application_course_id"),
    type: searchParams.get("type"),
    planId: searchParams.get("planId") || searchParams.get("plan_id"),
    tab: searchParams.get("tab"),
    refreshInvoices: searchParams.get("refreshInvoices"),
  };
}

export function hasCommissionDetailParams({
  studentId,
  appId,
  providerId,
  courseId,
  applicationCourseId,
  type,
}) {
  const hasCourse =
    Boolean(courseId && String(courseId).trim()) ||
    Boolean(applicationCourseId && String(applicationCourseId).trim());
  return Boolean(studentId && appId && providerId && hasCourse && type);
}

/** Build detail return URL after invoice create (no duplicate tab/refresh params). */
export function buildCommissionDetailInvoicesReturnUrl(
  returnTo,
  { planId, invoiceStatusTab } = {},
) {
  const fallback = "/commissions/detail";
  let url;
  try {
    url = new URL(returnTo || fallback, window.location.origin);
  } catch {
    url = new URL(fallback, window.location.origin);
  }

  url.searchParams.delete("tab");
  url.searchParams.delete("refreshInvoices");
  url.searchParams.delete("invoiceStatusTab");
  if (planId) url.searchParams.set("planId", String(planId));
  url.searchParams.set("tab", "invoices");
  if (invoiceStatusTab) {
    url.searchParams.set("invoiceStatusTab", String(invoiceStatusTab));
  }
  url.searchParams.set("refreshInvoices", String(Date.now()));

  return `${url.pathname}?${url.searchParams.toString()}`;
}

/** Strip invoice-return noise from a detail URL used as create-invoice returnTo. */
export function cleanCommissionDetailReturnTo(returnTo, { planId } = {}) {
  const fallback = "/commissions/detail";
  let url;
  try {
    url = new URL(returnTo || fallback, window.location.origin);
  } catch {
    url = new URL(fallback, window.location.origin);
  }

  url.searchParams.delete("tab");
  url.searchParams.delete("refreshInvoices");
  if (planId) url.searchParams.set("planId", String(planId));

  const qs = url.searchParams.toString();
  return qs ? `${url.pathname}?${qs}` : url.pathname;
}
